// components/templates/sections/OpeningHoursSection.tsx
'use client';
import Image from 'next/image';
import Link from 'next/link';
import { useDesign } from '@/context/DesignContext';

interface OpeningHours {
  weekdays: string;
  saturday: string;
  sunday: string;
  holidays: string;
  breakfast?: { weekdays: string; saturday: string; sunday: string; holidays: string };
  lunchDinner?: { weekdays: string; saturday: string; sunday: string; holidays: string };
}

interface OpeningHoursSectionProps {
  title: string;
  hours: OpeningHours;
  image: string;
  buttonText?: string;
  design?: any;
}

export default function OpeningHoursSection({
  title,
  hours,
  image,
  buttonText = 'book a table',
  design: propDesign,
}: OpeningHoursSectionProps) {
  const contextDesign = useDesign();
  const design = propDesign || contextDesign;

  const headingFont = design?.typography?.headingFont || "'Sora', sans-serif";
  const bodyFont = design?.typography?.bodyFont || "'Plus Jakarta Sans', sans-serif";
  const bgColor = design?.colors?.background || '#FFFFFF';
  const separatorColor = design?.colors?.border || '#E9ECEF';
  const btnStyle = design?.buttons?.outline || {
    class: 'tf-button style3',
    backgroundColor: 'transparent',
    borderColor: 'var(--primary)',
    textColor: 'var(--primary)',
    borderRadius: '30px',
    padding: '10px 25px',
  };
  const containerMaxWidth = design?.spacing?.containerMaxWidth || '1200px';

  const sectionStyle: React.CSSProperties = {
    backgroundColor: bgColor,
    padding: '60px 0',
  };
  const titleStyle: React.CSSProperties = {
    fontFamily: headingFont,
    fontSize: '2rem',
    fontWeight: 600,
    color: design?.colors?.text || '#1E1E1E',
    marginBottom: '1.5rem',
  };
  const listItemStyle: React.CSSProperties = {
    fontFamily: bodyFont,
    fontSize: '1rem',
    padding: '0.5rem 0',
    borderBottom: `1px solid ${separatorColor}`,
  };
  const buttonStyle: React.CSSProperties = {
    backgroundColor: btnStyle.backgroundColor,
    border: btnStyle.borderColor ? `1px solid ${btnStyle.borderColor}` : 'none',
    color: btnStyle.textColor,
    padding: btnStyle.padding,
    borderRadius: btnStyle.borderRadius,
    cursor: 'pointer',
    display: 'inline-block',
    marginTop: '1rem',
  };

  return (
    <section className="opening" style={sectionStyle}>
      <div className="shape"></div>
      <div className="container" style={{ maxWidth: containerMaxWidth, margin: '0 auto' }}>
        <div className="row">
          <div className="col-xl-7 col-md-12">
            <div className="opening-content">
              <div className="block-text">
                <h3 className="title" data-aos="fade-up" data-aos-duration="1000" style={titleStyle}>{title}</h3>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  <li data-aos="fade-up" data-aos-duration="1000" style={listItemStyle}><span>Monday - Friday :</span> {hours.weekdays}</li>
                  <li data-aos="fade-up" data-aos-duration="1000" style={listItemStyle}><span>Saturday :</span> {hours.saturday}</li>
                  <li data-aos="fade-up" data-aos-duration="1000" style={listItemStyle}><span>Sunday :</span> {hours.sunday}</li>
                  <li data-aos="fade-up" data-aos-duration="1000" style={listItemStyle}><span>Holidays :</span> {hours.holidays}</li>
                </ul>

                {(hours.breakfast || hours.lunchDinner) && (
                  <div className="d-flex">
                    {hours.breakfast && (
                      <ul style={{ listStyle: 'none', marginRight: '2rem' }}>
                        <h5 style={titleStyle}>Breakfast</h5>
                        <li style={listItemStyle}><span>Monday - Friday :</span> {hours.breakfast.weekdays}</li>
                        <li style={listItemStyle}><span>Saturday :</span> {hours.breakfast.saturday}</li>
                        <li style={listItemStyle}><span>Sunday :</span> {hours.breakfast.sunday}</li>
                        <li style={listItemStyle}><span>Holidays :</span> {hours.breakfast.holidays}</li>
                      </ul>
                    )}
                    {hours.lunchDinner && (
                      <ul style={{ listStyle: 'none' }}>
                        <h5 style={titleStyle}>Lunch and Dinner</h5>
                        <li style={listItemStyle}><span>Monday - Friday :</span> {hours.lunchDinner.weekdays}</li>
                        <li style={listItemStyle}><span>Saturday :</span> {hours.lunchDinner.saturday}</li>
                        <li style={listItemStyle}><span>Sunday :</span> {hours.lunchDinner.sunday}</li>
                        <li style={listItemStyle}><span>Holidays :</span> {hours.lunchDinner.holidays}</li>
                      </ul>
                    )}
                  </div>
                )}
                <Link href="#" className={btnStyle.class} data-aos="fade-up" data-aos-duration="1000" style={buttonStyle}>
                  {buttonText}
                </Link>
              </div>
            </div>
          </div>
          <div className="col-xl-5 col-md-12">
            <div className="opening-img" data-aos="fade-left" data-aos-duration="1000">
              <Image src={image} alt="" width={500} height={400} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}